import React, { FC, useEffect, useState } from 'react';
import { ApolloError, useMutation, useQuery } from '@apollo/client';
import { yupResolver } from '@hookform/resolvers/yup';
import { Typography, Box } from '@mui/material';
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';
import { styled } from '@mui/material/styles';
import {
  CustomFormulaForm,
  CustomFormulaValues,
  NumberType,
} from 'src/components/Forms/CustomFormulaForm';
import { customFormulaFormSchema } from 'src/components/Forms/validationSchemas';
import { ConfirmModal } from 'src/components/Modals/Confirm';
import { ROUTE_SOURCES } from 'src/constants/routeSources';
import { useSnackbar } from 'src/providers/snackbar/hook/useSnackbar';
import {
  formatCustomFormulaItems,
  unformatCustomFormulaItems,
} from 'src/utils';
import { UPDATE_CUSTOM_FORMULA } from 'src/utils/gql/mutations';
import { GET_CUSTOM_FORMULA_BY_ID } from 'src/utils/gql/queries';
import {
  MetricFormatType,
  Mutation,
  MutationUpdateCustomFormulaArgs,
  Query,
  QueryGetCustomFormulaByIdArgs,
} from 'src/utils/gql/types';

// Mantiene il contenuto fuori dalla sidebar senza usare 100vw, che taglia il lato destro.
const CustomFormulaContainer = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.background.default,
  minHeight: '100vh',
  padding: '24px',
  marginLeft: '240px',
  width: 'calc(100% - 240px)',
  maxWidth: 'calc(100% - 240px)',
  boxSizing: 'border-box',
  overflow: 'visible', // Permette overflow per evitare tagli

  [theme.breakpoints.down('lg')]: {
    marginLeft: '220px',
    width: 'calc(100% - 220px)',
    maxWidth: 'calc(100% - 220px)',
    padding: '20px',
  },

  [theme.breakpoints.down('md')]: {
    marginLeft: '200px',
    width: 'calc(100% - 200px)',
    maxWidth: 'calc(100% - 200px)',
    padding: '16px',
  },

  [theme.breakpoints.down('sm')]: {
    marginLeft: '0',
    width: '100%',
    padding: '16px',
    marginTop: '60px', // Spazio per header mobile
  },

  [theme.breakpoints.down('xs')]: {
    padding: '12px',
  },
}));

// Container interno completamente fluido e responsive
const ContentContainer = styled(Box)(({ theme }) => ({
  width: '100%',
  maxWidth: '100%',
  margin: '0 auto',
  padding: '0',
  boxSizing: 'border-box',
  overflow: 'visible', // Permette overflow per evitare tagli

  '.MuiTypography-h3': {
    marginBottom: '30px',

    [theme.breakpoints.down('sm')]: {
      marginBottom: '20px',
      textAlign: 'center',
    },
  },
}));

export const EditCustomFormula: FC = () => {
  const navigate = useNavigate();
  const snackbar = useSnackbar();
  const { formulaId = '' } = useParams();

  const [openModal, setOpenModal] = useState(false);

  const onError = (err: ApolloError) => {
    const { graphQLErrors, message: errorText } = err;
    const message =
      graphQLErrors && graphQLErrors.length > 0
        ? graphQLErrors[0].message
        : errorText;

    snackbar(message);
  };

  const { data: formulaData, loading: formulaLoading } = useQuery<
    Pick<Query, 'getCustomFormulaById'>,
    QueryGetCustomFormulaByIdArgs
  >(GET_CUSTOM_FORMULA_BY_ID, { variables: { id: formulaId }, onError });

  const [updateFormula, { loading: updatingFormula }] = useMutation<
    Pick<Mutation, 'updateCustomFormula'>,
    MutationUpdateCustomFormulaArgs
  >(UPDATE_CUSTOM_FORMULA, {
    onCompleted: () => {
      snackbar('A custom formula has been successfully updated.', 'success');
      navigate(`/${ROUTE_SOURCES.SETTINGS}`);
    },
    onError,
  });

  const formMethods = useForm<CustomFormulaValues>({
    mode: 'onChange',
    resolver: yupResolver(customFormulaFormSchema),
    criteriaMode: 'all',
    defaultValues: {
      name: '',
      formulaItems: [],
    },
  });

  const handleSubmit: SubmitHandler<CustomFormulaValues> = ({
    name,
    formulaItems,
    format,
  }) => {
    updateFormula({
      variables: {
        customFormulaData: {
          name,
          customFormulaItems: unformatCustomFormulaItems(formulaItems),
          format: {
            type: format.type,
            description: format.description || '',
            suffix:
              format.type === MetricFormatType.Number
                ? format.suffix
                : undefined,
            prefix:
              format.type === MetricFormatType.Number
                ? format.prefix
                : undefined,
            isDecimal:
              format.type === MetricFormatType.Number
                ? format.numberType === NumberType.Decimal
                : true,
          },
          id: formulaId,
        },
      },
    });
  };

  const handleCloseModal = () => setOpenModal(false);
  const handleClickCancel = () => setOpenModal(true);
  const handleConfirm = () => navigate(`/${ROUTE_SOURCES.SETTINGS}`);

  useEffect(() => {
    if (formulaData) {
      formMethods.reset({
        name: formulaData.getCustomFormulaById.name,
        formulaItems: formatCustomFormulaItems(
          formulaData.getCustomFormulaById.formulaData,
        ),
        format: {
          ...formulaData.getCustomFormulaById.format,
          numberType: formulaData.getCustomFormulaById.format.isDecimal
            ? NumberType.Decimal
            : NumberType.WholeNumber,
        },
      });
    }
  }, [formMethods, formulaData]);

  return (
    <>
      <CustomFormulaContainer>
        <ContentContainer>
          <Typography variant="h3">Edit Custom formula</Typography>
          <FormProvider {...formMethods}>
            <CustomFormulaForm
              onSubmit={handleSubmit}
              onCancel={handleClickCancel}
              buttonLoading={updatingFormula}
              inputLoading={formulaLoading}
            />
          </FormProvider>
        </ContentContainer>
      </CustomFormulaContainer>
      <ConfirmModal
        open={openModal}
        onClose={handleCloseModal}
        onConfirm={handleConfirm}
      />
    </>
  );
};
